import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Ban, CalendarPlus, Clock4, Copy, Loader2, Plus, Trash2, Wand2 } from "lucide-react";
import { api } from "../../lib/api";
import { checkKey, checkPayload, splitProblems } from "../../lib/groupCalendar";
import {
  MAX_SESSIONS, NOTE_MAX, WEEKDAYS, applyTimeToAll, checkSessions, emptySession,
  generateSessions, hoursLabel, listWarnings, sessionMinutes, totalMinutes, weekdayOf,
} from "../../lib/groupSessions";
import DatePickerField from "./DatePickerField";
import TimePickerField from "./TimePickerField";

const inp = "w-full rounded-lg border border-[#E5E7EB] bg-white px-2.5 py-1.5 text-sm text-[#373A45] focus:border-[#965EA7] focus:outline-none";
const quiet = "inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] px-3 py-1.5 text-xs font-semibold text-[#373A45] hover:bg-[#F5F6F6] disabled:opacity-40";

/**
 * The cohort's sessions, one row each: date, start, end, and an optional
 * public note ("Evaluare finală"). The generator fills the rows from a weekly
 * pattern; each row is then edited on its own — which is the point: Thursday
 * can run 17:00–20:00 while Tuesday runs 18:00–20:00.
 *
 * Red marks what the server would refuse: a row that is no timetable, or a
 * calendar rule of the methodology broken by a new or changed session —
 * checked by the server as the operator types, with the rule and the next
 * allowed day. Amber is the same rule on a session saved like that before,
 * and the 15-hour total: shown, never blocking.
 *
 * `onCalendar` receives the server's answer, so the form can refuse to save.
 */
export default function GroupSessionsEditor({ sessions, onChange, groupId, trainerId, maxSeats, onCalendar, regime }) {
  const [gen, setGen] = useState({ firstDate: "", days: [], count: 8, start: "18:00", end: "20:00" });
  const [showGen, setShowGen] = useState(sessions.length === 0);
  const [calendar, setCalendar] = useState({ problems: [], blocking: false });
  const [checking, setChecking] = useState(false);
  const asked = useRef("");

  const { errors } = checkSessions(sessions);
  const payload = checkPayload({ sessions, groupId, trainerId, maxSeats, regime });
  const key = checkKey(payload);

  // Ask the server a moment after the typing stops; only the last answer
  // counts, so a slow reply never overwrites a newer one.
  useEffect(() => {
    const timer = setTimeout(async () => {
      asked.current = key;
      setChecking(true);
      try {
        const r = await api.post("/admin/group-calendar/check", JSON.parse(key));
        if (asked.current !== key) return;
        setCalendar(r.data);
        onCalendar?.({ ...r.data, key });
      } catch (e) {
        if (asked.current !== key) return;
        // Unreachable for a moment: say nothing here, the save checks again.
        setCalendar({ problems: [], blocking: false });
        onCalendar?.({ problems: [], blocking: false, key });
      } finally {
        if (asked.current === key) setChecking(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const { byRow, group: groupProblems } = splitProblems(calendar.problems);
  const split = calendar.split || { rows: {} };
  const fmt = (x) => String(Math.round(Number(x || 0) * 100) / 100).replace(".", ",");
  const total = totalMinutes(sessions);
  const overall = listWarnings(sessions);

  const update = (i, patch) => onChange(sessions.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const remove = (i) => onChange(sessions.filter((_, k) => k !== i));
  const add = () => {
    const last = sessions[sessions.length - 1];
    onChange([...sessions, last ? { ...emptySession(), start_time: last.start_time, end_time: last.end_time } : emptySession()]);
  };

  const generate = () => {
    const rows = generateSessions(gen);
    if (!rows.length) return;
    if (sessions.length && !window.confirm(`Înlocuiești cele ${sessions.length} sesiuni cu ${rows.length} sesiuni generate?`)) return;
    onChange(rows);
    setShowGen(false);
  };

  const toggleGenDay = (d) => setGen((g) => ({
    ...g, days: g.days.includes(d) ? g.days.filter((x) => x !== d) : [...g.days, d],
  }));

  const first = sessions[0];
  const canApplyTime = first && first.start_time && first.end_time && sessions.length > 1;

  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-[#FAFAFB] p-3" data-testid="group-sessions-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-[#373A45]">
          Sesiuni <span className="font-normal text-[#6B7280]">({sessions.length})</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={quiet} onClick={() => setShowGen((v) => !v)} data-testid="sessions-generator-toggle">
            <Wand2 size={13} /> Generează din zile
          </button>
          <button type="button" className={quiet} disabled={!canApplyTime}
            onClick={() => onChange(applyTimeToAll(sessions, first))}
            title="Copiază ora primei sesiuni pe toate celelalte" data-testid="sessions-apply-time">
            <Copy size={13} /> Aplică ora primei sesiuni tuturor
          </button>
        </div>
      </div>

      {showGen && (
        <div className="mt-3 rounded-lg border border-[#965EA7]/25 bg-white p-3" data-testid="sessions-generator">
          <div className="grid gap-3 sm:grid-cols-4">
            <label className="text-xs text-[#6B7280] sm:col-span-2">
              Prima sesiune
              <div className="mt-1">
                <DatePickerField size="sm" value={gen.firstDate} ariaLabel="Data primei sesiuni"
                  onChange={(v) => setGen({ ...gen, firstDate: v })} testid="sessions-gen-first" />
              </div>
            </label>
            <label className="text-xs text-[#6B7280]">
              Număr de sesiuni
              <input type="number" min="1" max={MAX_SESSIONS} className={`${inp} mt-1`} value={gen.count}
                onChange={(e) => setGen({ ...gen, count: e.target.value })} data-testid="sessions-gen-count" />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-[#6B7280]">
                De la
                <div className="mt-1">
                  <TimePickerField value={gen.start} ariaLabel="Ora de început" onChange={(v) => setGen({ ...gen, start: v })}
                    testid="sessions-gen-start" />
                </div>
              </label>
              <label className="text-xs text-[#6B7280]">
                Până la
                <div className="mt-1">
                  <TimePickerField value={gen.end} ariaLabel="Ora de final" onChange={(v) => setGen({ ...gen, end: v })}
                    testid="sessions-gen-end" />
                </div>
              </label>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs text-[#6B7280]">În zilele:</span>
            {WEEKDAYS.map((d) => {
              const on = gen.days.includes(d);
              return (
                <button key={d} type="button" onClick={() => toggleGenDay(d)} aria-pressed={on}
                  className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
                    on ? "border-[#965EA7] bg-[#965EA7] text-white" : "border-[#E5E7EB] bg-white text-[#373A45] hover:border-[#965EA7]/60"}`}>
                  {d}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center justify-between gap-2">
            <p className="text-[11px] text-[#6B7280]">După generare poți modifica fiecare sesiune separat.</p>
            <button type="button" onClick={generate}
              disabled={!gen.firstDate || !gen.days.length || !gen.count || !gen.start || !gen.end}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#7A4986] disabled:opacity-40"
              data-testid="sessions-gen-run">
              <CalendarPlus size={13} /> Generează sesiunile
            </button>
          </div>
        </div>
      )}

      {sessions.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm" data-testid="sessions-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-[#6B7280]">
                <th className="w-8 py-1 font-semibold">#</th>
                <th className="w-40 py-1 font-semibold">Data</th>
                <th className="w-28 py-1 font-semibold">Început</th>
                <th className="w-28 py-1 font-semibold">Final</th>
                <th className="py-1 font-semibold">Mențiune (opțional, apare public)</th>
                <th className="w-44 py-1 font-semibold">Link propriu (opțional)</th>
                <th className="w-32 py-1 font-semibold" title="Componenta teoretică și cea practică, în ore">Teoretic / practic</th>
                <th className="w-8 py-1" />
              </tr>
            </thead>
            <tbody>
              {sessions.map((s, i) => {
                const mins = sessionMinutes(s);
                return (
                  <React.Fragment key={i}>
                    <tr className="align-top" data-testid={`session-row-${i}`}>
                      <td className="py-1.5 pr-2 text-xs tabular-nums text-[#6B7280]">{i + 1}</td>
                      <td className="py-1.5 pr-2">
                        <DatePickerField size="sm" value={s.date} ariaLabel={`Data sesiunii ${i + 1}`}
                          contextDate={s.date || sessions[i - 1]?.date || undefined}
                          onChange={(v) => update(i, { date: v })} testid={`session-date-${i}`} />
                        <div className="mt-0.5 text-[11px] text-[#6B7280]">{weekdayOf(s.date) || " "}</div>
                      </td>
                      <td className="py-1.5 pr-2">
                        <TimePickerField value={s.start_time} ariaLabel={`Ora de început a sesiunii ${i + 1}`}
                          onChange={(v) => update(i, { start_time: v })} testid={`session-start-${i}`} />
                      </td>
                      <td className="py-1.5 pr-2">
                        <TimePickerField value={s.end_time} ariaLabel={`Ora de final a sesiunii ${i + 1}`}
                          onChange={(v) => update(i, { end_time: v })} testid={`session-end-${i}`} />
                        {mins > 0 && <div className="mt-0.5 text-[11px] text-[#6B7280] tabular-nums">{hoursLabel(mins)}</div>}
                      </td>
                      <td className="py-1.5 pr-2">
                        <input className={inp} value={s.note || ""} maxLength={NOTE_MAX} placeholder="ex. Evaluare finală"
                          aria-label={`Mențiune pentru sesiunea ${i + 1}`}
                          onChange={(e) => update(i, { note: e.target.value })} data-testid={`session-note-${i}`} />
                      </td>
                      <td className="py-1.5 pr-2">
                        {/* Only when this session happens somewhere else than
                            the rest — otherwise the cohort's own link is used. */}
                        <input className={inp} value={s.access_link || ""} maxLength={500}
                          placeholder="alt link (opțional)"
                          aria-label={`Link propriu pentru sesiunea ${i + 1}`}
                          onChange={(e) => update(i, { access_link: e.target.value })}
                          data-testid={`session-link-${i}`} />
                      </td>
                      <td className="py-1.5 pr-2">
                        {/* Empty: the curriculum's split, theme by theme. Both
                            written: this session's own (PR 8b). */}
                        <div className="flex items-center gap-1">
                          <input className={`${inp} w-12 px-1.5 text-center tabular-nums`} inputMode="decimal"
                            value={s.theory_hours ?? ""} placeholder={split.rows[i] ? fmt(split.rows[i].theory) : "T"}
                            aria-label={`Ore teoretice, sesiunea ${i + 1}`} data-testid={`session-theory-${i}`}
                            onChange={(e) => update(i, { theory_hours: e.target.value })} />
                          <span className="text-[#6B7280]">/</span>
                          <input className={`${inp} w-12 px-1.5 text-center tabular-nums`} inputMode="decimal"
                            value={s.practice_hours ?? ""} placeholder={split.rows[i] ? fmt(split.rows[i].practice) : "P"}
                            aria-label={`Ore practice, sesiunea ${i + 1}`} data-testid={`session-practice-${i}`}
                            onChange={(e) => update(i, { practice_hours: e.target.value })} />
                        </div>
                        {split.rows[i]?.themes?.length > 0 && !split.rows[i].own && (
                          <div className="mt-0.5 text-[11px] text-[#6B7280]">temele {split.rows[i].themes.join(", ")}</div>
                        )}
                      </td>
                      <td className="py-1.5 text-right">
                        <button type="button" onClick={() => remove(i)} aria-label={`Șterge sesiunea ${i + 1}`}
                          className="rounded-full p-1.5 text-[#9b1f2c] hover:bg-[#FDECEC]" data-testid={`session-remove-${i}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                    {(errors[i] || byRow[i]) && (
                      <tr>
                        <td />
                        <td colSpan={7} className="pb-2">
                          {errors[i] && <div className="text-[11px] font-semibold text-[#9b1f2c]" data-testid={`session-error-${i}`}>{errors[i]}</div>}
                          {!errors[i] && (byRow[i] || []).map((p) => <CalendarProblem key={p.code} problem={p} testid={`session-rule-${i}-${p.code}`} />)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <button type="button" className={quiet} onClick={add} disabled={sessions.length >= MAX_SESSIONS} data-testid="sessions-add">
          <Plus size={13} /> Adaugă sesiune
        </button>
        {sessions.length > 0 && (
          <div className="inline-flex items-center gap-1.5 text-xs text-[#373A45]" data-testid="sessions-total">
            <Clock4 size={13} className="text-[#6B7280]" />
            Total: <strong className="tabular-nums">{hoursLabel(total)}</strong> în {sessions.length} {sessions.length === 1 ? "sesiune" : "sesiuni"}
          </div>
        )}
      </div>
      {sessions.length > 0 && split.curriculum && (
        <div className={`mt-2 text-[11px] ${Math.abs(split.theory - split.curriculum.theory) > 0.01 || Math.abs(split.practice - split.curriculum.practice) > 0.01
          ? "text-[#7A5A1B]" : "text-[#6B7280]"}`} data-testid="sessions-split-total">
          Pe componente: teoretic {fmt(split.theory)} h din {fmt(split.curriculum.theory)}, practic sincron{" "}
          {fmt(split.practice)} h din {fmt(split.curriculum.practice)}. Împărțirea vine din curriculum, temă cu temă;
          completează teoretic/practic doar la o sesiune care se abate.
        </div>
      )}
      {groupProblems.map((p) => (
        <div key={p.code} className="mt-2"><CalendarProblem problem={p} boxed testid={`sessions-rule-${p.code}`} /></div>
      ))}
      {checking && (
        <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#6B7280]" data-testid="sessions-calendar-checking">
          <Loader2 size={11} className="animate-spin" /> Verific regulile de calendar…
        </div>
      )}
      {overall.map((w) => (
        <div key={w} className="mt-2 inline-flex items-start gap-1.5 rounded-lg border border-[#F5C76E]/40 bg-[#FFF7E6] px-2.5 py-1.5 text-[11px] text-[#7A5A1B]"
          data-testid="sessions-total-warning">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {w}
        </div>
      ))}
      <p className="mt-2 text-[11px] text-[#6B7280]">
        Data de început și de final ale grupei se calculează din prima și din ultima sesiune. Sesiunile se ordonează după dată la salvare.
      </p>
    </div>
  );
}

/**
 * One calendar rule: what is wrong, the next allowed day when there is one,
 * and the article — small, under it, because it is what an inspector asks.
 * Red when it blocks the save, amber when the session was saved like that
 * before (to fix on purpose, not by surprise).
 */
function CalendarProblem({ problem, boxed = false, testid }) {
  const tone = problem.blocking ? "text-[#9b1f2c]" : "text-[#7A5A1B]";
  const box = boxed
    ? problem.blocking
      ? "rounded-lg border border-[#9b1f2c]/20 bg-[#FDECEC] px-2.5 py-1.5"
      : "rounded-lg border border-[#F5C76E]/40 bg-[#FFF7E6] px-2.5 py-1.5"
    : "";
  const Icon = problem.blocking ? Ban : AlertTriangle;
  return (
    <div className={`flex items-start gap-1.5 text-[11px] ${tone} ${box}`} data-testid={testid}>
      <Icon size={11} className="mt-0.5 shrink-0" />
      <span>
        <span className={problem.blocking ? "font-semibold" : ""}>{problem.message}</span>
        {problem.suggestion && <span> {problem.suggestion}</span>}
        {!problem.blocking && problem.index !== null && problem.index !== undefined && (
          <span> Sesiunea era salvată așa; o poți lăsa, dar ar trebui corectată.</span>
        )}
        <span className="block text-[10px] opacity-75">{problem.rule}</span>
      </span>
    </div>
  );
}
